import {Group} from 'src/design/atoms/Group';
import {v4 as uuidv4} from 'uuid';
import React, {useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';

import {
  FeatureFlagEnum,
  ModuleEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {useCreateTaskDefinitionMutation} from 'src/redux/queries/taskDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkActions} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ActionForm, useActionForm} from 'src/app/actions/ActionForm';
import {TasksOfflineBoundary} from 'src/app/tasks/TasksOfflineBoundary';
import {TaskStateBanner} from 'src/app/tasks/TaskStateBanner';
import {useActionsBreadcrumbs} from '../hooks/useActionsBreadcrumbs';
import {
  ActionFormMetaRule,
  ActionsDocumentTitle,
  MapActionFormDataToTaskDefinitionRequest,
} from 'src/app/actions/actionHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {isOfflineQueuedResult} from 'src/offline/offlineMutation';
import type {IStateMachine} from 'src/types/StateMachine';
import type {IStateMachineTransition} from 'src/types/StateMachine';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const CreateActionPage: React.FC = () => {
  const taskType = TaskTypeEnum.Actions;
  const module = ModuleEnum.Actions;
  const featureFlag = FeatureFlagEnum.Actions;

  const [searchParams] = useSearchParams();
  const formId = searchParams.get('formId');

  useSetDocumentTitle(ActionsDocumentTitle);

  const {backBarProps} = useActionsBreadcrumbs({mode: 'create'});
  const {returnTo} = useBackNavigation(backBarProps.fallbackTo);

  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState<boolean>(false);

  const [createTaskDefinition] = useCreateTaskDefinitionMutation();

  const {formApi, isLoading: isActionFormLoading} = useActionForm({
    metarules: [ActionFormMetaRule.UseDueDate],
  });

  const taskId = useRef<number | null>(null);
  const transitionUrl = `task-definition/${taskId.current}/transition`;

  const submit = async (transition?: IStateMachineTransition) => {
    if (!(await formApi.validateForm())) {
      setAlert('Please fix the errors in the form', 'error');
      return {continue: false};
    }

    setIsSavePending(true);

    const body = MapActionFormDataToTaskDefinitionRequest(
      formApi.methods.getValues(),
      taskType,
      formId
    );

    try {
      const resp = await createTaskDefinition({
        ...body,
        clientSubmissionId: uuidv4(),
        // Queued offline the publish rides with the create; online it is the
        // transition bar's own POST, as before.
        queuedTransition: transition ? {name: transition.name} : undefined,
      }).unwrap();

      if (isOfflineQueuedResult(resp)) {
        setAlert('Action saved on this device. It will sync when you are back online.', 'success');
        setIsSavePending(false);
        return {continue: false, offlineQueued: true};
      }

      setAlert(`Action was successfully created`, 'success');

      taskId.current = resp.id;
      return {
        continue: true,
        overrideTransitionBaseUrl: `task-definition/${taskId.current}/transition`,
      };
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
      return {continue: false};
    }
  };

  const onSubmitted = () =>
    returnTo(PathUtility.ActionView(String(taskId.current), 'content'));

  // A queued create has no id yet; its pending row is on the my-actions tab.
  const onOfflineQueued = () => returnTo(PathUtility.Actions('user'));

  const handleSave = async () => {
    if (await formApi.validateForm()) {
      const result = await submit();
      if (result.offlineQueued) {
        onOfflineQueued();
      } else if (result.continue) {
        onSubmitted();
      }
    } else {
      setAlert('Please fix the errors in the form', 'error');
    }
  };

  const [machineBase] = useStateMachine({
    url: ApiEndpoints.getTaskDefinitionStateMachine,
  });
  const machine = machineBase
    ? ({
        states: machineBase.states,
        transitions: machineBase.transitions.map(t => ({
          ...t,
          title: `Save & ${t.title}`,
        })),
      } as IStateMachine)
    : undefined;

  return (
    <TasksOfflineBoundary>
    <ContentWrapper featureFlag={featureFlag}>
      <BackBar {...backBarProps} />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkActions.icon}>
          New Action
        </PageHeader.Title>
      </PageHeader>

      <TaskStateBanner module={module} state={TaskDefinitionStateEnum.Draft} />

      <ActionForm form={formApi} isLoading={isActionFormLoading} />
      <Group align="start">
        {machine && (
          <FormTransitionBar
            preTransitionButtonNodes={[
              <SaveButton
                loading={isSavePending}
                onClick={handleSave}
                mr="md"
              />,
            ]}
            onTransitioning={submit}
            onOfflineQueued={onOfflineQueued}
            module={module}
            stateMachine={machine}
            stateId={TaskDefinitionStateEnum.Draft}
            transitionBaseUrl={transitionUrl}
            onRefreshTransition={onSubmitted}
            bypassPermissions={true}
            invalidateTagsOnComplete={['TaskDefinitions', 'TaskAssignments']}
          />
        )}
      </Group>
    </ContentWrapper>
    </TasksOfflineBoundary>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.ActionCreate(),
  element: <CreateActionPage />,
  isPrivate: true,
  supportsOffline: true,
});

export default CreateActionPage;
